import { TranslocoService } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';

/**
 * Formats a byte size into a human-readable string.
 * @param bytes - The size in bytes
 * @param si - If true, use SI units (1000), otherwise use binary units (1024)
 * @param dp - Number of decimal places
 * @returns Formatted string like "1.5 MiB" or "1.5 MB"
 */
export function humanFileSize(bytes = 0, si = true, dp = 2): string {
    const thresh = si ? 1000 : 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

    return bytes.toFixed(dp) + ' ' + units[u];
}

/**
 * Formats a date string using TranslocoDatePipe with locale support.
 * Handles invalid dates (null, empty, or year <= 1) gracefully.
 * @param pipe - The TranslocoDatePipe instance
 * @param dateString - The date string to format
 * @returns Formatted date string or empty string if invalid
 */
export function formatDateWithTransloco(pipe: TranslocoDatePipe, dateString: Date | string | null, showTime = true): string {
    if (!dateString) return '';
    try {
        // if date is not coming from grid (odata => Date obj) it is incorrect format (string) and has to be converted
        let normalized = dateString;
        if (typeof dateString === 'string') {
            if (dateString.endsWith('Z')) {
                normalized = dateString.slice(0, -1);
            } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                normalized = dateString + 'T00:00:00';
            }
        }
        const date = new Date(normalized as string);
        if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1) {
            return '';
        }
        const options = showTime ? { dateStyle: 'short' as const, timeStyle: 'short' as const } : { dateStyle: 'short' as const };
        return pipe.transform(date, options) || '';
    } catch {
        return '';
    }
}

/**
 * Converts a date picked in local time (e.g. from a dx-date-box) to a UTC
 * midnight ISO string while preserving the chosen calendar day.
 * Avoids the "-1 day" shift that value.toISOString() causes in CET/CEST.
 */
export function localDateToUtcMidnightIso(value: Date): string {
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())).toISOString();
}

export function formatBoolean(translocoService: TranslocoService, value: boolean): string {
    if (value === null || value === undefined) return '';
    return translocoService.translate(value ? 'yes' : 'no');
}
